import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that renders quick-buy size variant pills on product cards.
 * Each pill directly adds the specific size variant to cart via AJAX,
 * reusing the existing product-form-component infrastructure.
 *
 * @extends {Component}
 */
export class QuickBuySizes extends Component {
  /** @type {HTMLButtonElement | null} */
  #activePill = null;

  /** @type {number | null} */
  #successTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handlePillClick);
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);

    if (this.#successTimeout) {
      clearTimeout(this.#successTimeout);
      this.#successTimeout = null;
    }
  }

  /**
   * Handles click events on pill buttons.
   * @param {Event} event
   */
  #handlePillClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const pill = event.target.closest('.quick-buy-sizes__pill');
    if (!(pill instanceof HTMLButtonElement)) return;

    // Prevent navigation on the product card
    event.preventDefault();
    event.stopPropagation();

    if (pill.disabled || pill.dataset.loading === 'true') return;

    const variantId = pill.dataset.variantId;
    const quantity = pill.dataset.quantity || '1';

    if (!variantId) return;

    this.#activePill = pill;
    this.#setPillLoading(pill, true);

    // Update the hidden form inputs
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[ref="variantId"]')
    );
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="quantity"]')
    );

    if (variantIdInput) {
      variantIdInput.value = variantId;
    }

    if (quantityInput) {
      quantityInput.value = quantity;
    }

    // Trigger form submission via the product-form-component
    const form = this.querySelector('form');
    if (form) {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  /**
   * Handles successful cart add events.
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    // Only handle events from our own product-form-component
    if (event.detail?.source !== 'product-form-component') return;

    const formComponent = this.querySelector('product-form-component');
    if (!formComponent) return;

    // Check if this event came from our form component
    const eventTarget = /** @type {HTMLElement} */ (event.target);
    if (eventTarget !== formComponent) return;

    if (!this.#activePill) return;

    this.#setPillLoading(this.#activePill, false);
    this.#setPillSuccess(this.#activePill, true);

    // Reset success state after animation
    this.#successTimeout = window.setTimeout(() => {
      if (this.#activePill) {
        this.#setPillSuccess(this.#activePill, false);
        this.#activePill = null;
      }
    }, 1500);
  };

  /**
   * Handles cart add error events.
   * @param {CartErrorEvent} event
   */
  #handleCartError = (event) => {
    // Only handle events from our own product-form-component
    const formComponent = this.querySelector('product-form-component');
    if (!formComponent) return;

    const eventTarget = /** @type {HTMLElement} */ (event.target);
    if (eventTarget !== formComponent) return;

    if (!this.#activePill) return;

    this.#setPillLoading(this.#activePill, false);
    this.#setPillError(this.#activePill, true);

    // Re-enable pill after a delay, unless it's truly unavailable
    setTimeout(() => {
      if (this.#activePill) {
        this.#setPillError(this.#activePill, false);
        this.#activePill = null;
      }
    }, 2000);
  };

  /**
   * Sets the loading state on a pill.
   * @param {HTMLButtonElement} pill
   * @param {boolean} loading
   */
  #setPillLoading(pill, loading) {
    pill.dataset.loading = loading ? 'true' : 'false';
    pill.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  /**
   * Sets the success state on a pill.
   * @param {HTMLButtonElement} pill
   * @param {boolean} success
   */
  #setPillSuccess(pill, success) {
    pill.dataset.added = success ? 'true' : 'false';
  }

  /**
   * Sets the error state on a pill.
   * @param {HTMLButtonElement} pill
   * @param {boolean} error
   */
  #setPillError(pill, error) {
    pill.dataset.error = error ? 'true' : 'false';
  }
}

if (!customElements.get('quick-buy-sizes')) {
  customElements.define('quick-buy-sizes', QuickBuySizes);
}
